// client/src/components/CashierSettings.js

import React, { useState, useEffect, useRef } from 'react';
import { firestore, storage } from '../../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, getDownloadURL, listAll, getMetadata } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { FaDownload, FaLifeRing, FaEnvelope, FaSignOutAlt } from 'react-icons/fa';
import CashierNavbar from './CashierNavbar';
import '../CSS/Dashboard.css';
import { logoutSafely } from '../../utils/logoutSafely';

// Fond du QR code (unique pour le composant mémoïsé)
const QR_BG_SRC = 'https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2FNOUVEAU%20MODEL%20ECOBILL.png?alt=media&token=2bc91f87-0459-4832-8ae0-e3b88464dba0';

// Composant extrait en dehors du render pour conserver une identité stable et éviter les re-montages
const QRCodeDisplay = React.memo(function QRCodeDisplay({ value, caption }) {
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const internalSize = isMobile ? 500 : 1750;
  const displaySize = isMobile ? 80 : 110;
  const containerWidth = isMobile ? '110px' : '200px';

  return (
    <div id="qr-container" style={{ position: 'relative', display: 'inline-block', width: containerWidth }}>
      <img crossOrigin="anonymous" src={QR_BG_SRC} alt="Fond QR Code" style={{ display: 'block', width: '100%', height: 'auto' }} />
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <QRCodeCanvas
            id="qr-code-canvas"
            value={value}
            size={internalSize}
            level="H"
            includeMargin={false}
            bgColor="transparent"
            style={{ width: `${displaySize}px`, height: `${displaySize}px` }}
          />
          {caption ? (
            <div
              style={{
                marginTop: isMobile ? 4 : 6,
                fontSize: isMobile ? '9px' : '12px',
                lineHeight: 1.1,
                color: 'var(--eb-text)',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                textAlign: 'center',
              }}
            >
              {caption}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

/* ============================
   Cache utils (TTL = 5 minutes)
============================ */
const CACHE_TTL_MS = 5 * 60 * 1000;

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.fetchedAt) return null;
    if (Date.now() - obj.fetchedAt > CACHE_TTL_MS) return null;
    return obj.data ?? null;
  } catch { return null; }
};

const writeCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ data, fetchedAt: Date.now() }));
  } catch {}
};

const CashierSettings = () => {
  // Pseudo utilisateur
  const [pseudo, setPseudo] = useState('');
  // Pseudo de l’entreprise (users/{companyuid})
  const [companyPseudo, setCompanyPseudo] = useState('');
  // UID de l’entreprise (pour QR)
  const [companyUid, setCompanyUid] = useState('');
  // URL courte (ShortID de fidelity/{companyuid})
  const [shortId, setShortId] = useState('');
  // URL de la photo de profil ("<companyuid>/photo-profil/")
  const [profilePhotoURL, setProfilePhotoURL] = useState(null);
  // Email du référent (users/{companyuid}.email)
  const [referentEmail, setReferentEmail] = useState('');

  const navigate = useNavigate();
  const auth = getAuth();
  const lastCompanyUidRef = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  /* ============================
     Auth + écoute user courant
     (inchangé côté UX)
  ============================ */
  useEffect(() => {
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (unsubscribeUser) { unsubscribeUser(); unsubscribeUser = null; }

      if (!currentUser) {
        navigate('/logingeneral');
        return;
      }

      const userDocRef = doc(firestore, 'users', currentUser.uid);
      unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
        const userData = docSnapshot.data() || {};

        // 1) Pseudo utilisateur (fallback email)
        const nextPseudo = userData.pseudo || currentUser.email || '';
        setPseudo((prev) => (prev === nextPseudo ? prev : nextPseudo));

        // 2) Company UID
        if (userData.companyuid && userData.companyuid !== lastCompanyUidRef.current) {
          lastCompanyUidRef.current = userData.companyuid;
          setCompanyUid(userData.companyuid);
        }
      });
    });

    return () => {
      if (unsubscribeUser) unsubscribeUser();
      unsubscribeAuth();
    };
  }, [auth, navigate]);

  /* ============================
     Fetch combiné pour company data
     - users/{companyUid} → pseudo + email (1 seule lecture)
     - fidelity/{companyUid} → ShortID (cache TTL)
     - storage "{uid}/photo-profil" → dernière image (métadonnées puis 1 URL)
     (avec cache sur toutes les parties)
  ============================ */
  useEffect(() => {
    const uid = companyUid;
    if (!uid) return;

    let cancelled = false;

    const run = async () => {
      // 0) Essayer le cache global
      const cacheKey = `settings_company_${uid}`;
      const cached = readCache(cacheKey);
      if (cached) {
        if (!cancelled && !unmountedRef.current) {
          setCompanyPseudo((prev) => (prev === cached.companyPseudo ? prev : cached.companyPseudo || ''));
          setReferentEmail((prev) => (prev === cached.referentEmail ? prev : cached.referentEmail || ''));
          setShortId((prev) => (prev === cached.shortId ? prev : cached.shortId || ''));
          setProfilePhotoURL((prev) => (prev === cached.profilePhotoURL ? prev : cached.profilePhotoURL || null));
        }
      }

      // 1) users/{companyUid} (pseudo + email) — 1 getDoc
      let nextCompanyPseudo = cached?.companyPseudo ?? '';
      let nextReferentEmail = cached?.referentEmail ?? '';

      try {
        if (!cached || !cached.companyPseudo || !cached.referentEmail) {
          const companyDocRef = doc(firestore, 'users', uid);
          const companySnap = await getDoc(companyDocRef);
          if (companySnap.exists()) {
            const d = companySnap.data();
            nextCompanyPseudo = d?.pseudo || '';
            nextReferentEmail = d?.email || '';
          } else {
            nextCompanyPseudo = '';
            nextReferentEmail = '';
          }
        }
      } catch (e) {
        console.error('Erreur users/{companyUid} :', e);
      }

      if (!cancelled && !unmountedRef.current) {
        setCompanyPseudo((prev) => (prev === nextCompanyPseudo ? prev : nextCompanyPseudo));
        setReferentEmail((prev) => (prev === nextReferentEmail ? prev : nextReferentEmail));
      }

      // 2) fidelity/{companyUid} (ShortID) — 1 getDoc si pas en cache
      let nextShortId = cached?.shortId ?? '';
      try {
        if (!cached || !cached.shortId) {
          const fidRef = doc(firestore, 'fidelity', uid);
          const fidSnap = await getDoc(fidRef);
          nextShortId = fidSnap.exists() ? (fidSnap.data()?.ShortID || '') : '';
        }
      } catch (e) {
        console.error('Erreur fidelity/{companyUid} ShortID :', e);
      }

      if (!cancelled && !unmountedRef.current) {
        setShortId((prev) => (prev === nextShortId ? prev : nextShortId));
      }

      // 3) Storage — dernière photo (métadonnées puis 1 URL) si pas en cache
      let nextProfilePhotoURL = cached?.profilePhotoURL ?? null;
      try {
        if (!cached || !cached.profilePhotoURL) {
          const folderRef = ref(storage, `${uid}/photo-profil`);
          const listResult = await listAll(folderRef);

          if (listResult.items.length > 0) {
            // lire juste les métadonnées pour trier
            const metas = await Promise.all(
              listResult.items.map(async (fileRef) => {
                const meta = await getMetadata(fileRef);
                const timeCreated = meta.timeCreated ? new Date(meta.timeCreated) : new Date(0);
                return { fileRef, timeCreated };
              })
            );
            metas.sort((a, b) => b.timeCreated - a.timeCreated);
            // 1 seul getDownloadURL (le plus récent)
            nextProfilePhotoURL = await getDownloadURL(metas[0].fileRef);
          } else {
            nextProfilePhotoURL = null;
          }
        }
      } catch (e) {
        console.error('Erreur Storage photo-profil :', e);
      }

      if (!cancelled && !unmountedRef.current) {
        setProfilePhotoURL((prev) => (prev === nextProfilePhotoURL ? prev : nextProfilePhotoURL));
      }

      // 4) Écrire en cache (écrase l’ancien TTL)
      writeCache(cacheKey, {
        companyPseudo: nextCompanyPseudo,
        referentEmail: nextReferentEmail,
        shortId: nextShortId,
        profilePhotoURL: nextProfilePhotoURL
      });
    };

    run();
    return () => { cancelled = true; };
  }, [companyUid]);

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await logoutSafely(auth);
    } catch (error) {
      console.error('Erreur lors de la déconnexion :', error?.message || error);
    } finally {
      try {
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
      } catch {}
      window.location.href = '/';
    }
  };

  // Téléchargement du QR Code en haute qualité (inchangé visuellement)
  const handleDownloadQRCode = () => {
    const finalWidth = 1748;
    const finalHeight = 2480;
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = finalWidth;
    finalCanvas.height = finalHeight;
    const ctx = finalCanvas.getContext('2d');

    const bgImage = new Image();
    bgImage.crossOrigin = 'anonymous';
    bgImage.src =
      'https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2FNOUVEAU%20MODEL%20ECOBILL.png?alt=media&token=2bc91f87-0459-4832-8ae0-e3b88464dba0';

    bgImage.onload = () => {
      ctx.drawImage(bgImage, 0, 0, finalWidth, finalHeight);

      const qrCanvas = document.getElementById('qr-code-canvas');
      const qrFinalWidth = finalWidth * 0.55;
      const qrFinalHeight = qrFinalWidth;
      const qrX = (finalWidth - qrFinalWidth) / 2;
      const qrY = (finalHeight - qrFinalHeight) / 2;

      if (qrCanvas) {
        ctx.drawImage(qrCanvas, qrX, qrY, qrFinalWidth, qrFinalHeight);
      }

      if (shortId) {
        const caption = `ecobill.app/${shortId}`;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#111';
        const fontSize = Math.round(finalWidth * 0.028);
        ctx.font = `bold ${fontSize}px monospace`;
        const textX = finalWidth / 2;
        const textY = qrY + qrFinalHeight + Math.round(finalHeight * 0.015);
        ctx.fillText(caption, textX, textY);
        ctx.restore();
      }

      const dataUrl = finalCanvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'QR-CODE_ECOBILL.png';
      link.click();
    };

    bgImage.onerror = (error) => {
      console.error('Erreur lors du chargement de l\'image de fond', error);
    };
  };

  return (
    <div>
      <CashierNavbar />
      <div className="dashboard-container">
        <style>{`
          .cs-page { width: 100%; display: flex; flex-direction: column; gap: 20px; }
          .cs-page .eb-page-header { margin-bottom: 4px; }
          .cs-page .eb-page-title { margin: 0; text-align: left; }
          .cs-identity { padding: 24px; display: flex; align-items: center; gap: 20px; }
          .cs-avatar { width: 88px; height: 88px; border-radius: 50%; object-fit: cover; border: 1px solid var(--eb-border); box-shadow: var(--eb-shadow-sm); flex-shrink: 0; }
          .cs-company { margin: 0; font-size: 20px; font-weight: 700; letter-spacing: -0.01em; color: var(--eb-text); }
          .cs-user { margin: 4px 0 0; font-size: 15px; font-weight: 500; color: var(--eb-muted); word-break: break-word; }
          .cs-qr { display: flex; flex-direction: column; text-align: center; }
          .cs-qr .eb-card__body { display: flex; flex-direction: column; align-items: center; gap: 16px; }
          .cs-layout { align-items: start; }
          .dashboard-container .cs-section-title { margin: 0; text-align: left; }
          .cs-help { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr)); gap: 16px; }
          .cs-help__card { padding: 20px; display: flex; flex-direction: column; gap: 6px; }
          .cs-help__title { margin: 0; display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 650; color: var(--eb-text); }
          .cs-help__title svg { color: var(--eb-brand); }
          .cs-help__text { margin: 0; font-size: 14px; color: var(--eb-muted); word-break: break-word; }
          .cs-help__text a { font-weight: 600; }
          .cs-session { padding: 4px 20px; }
          .cs-session .logout-container { width: auto; margin: 0; }
          .cs-session__title { margin: 0; font-size: 14.5px; font-weight: 600; color: var(--eb-text); }
          .cs-session__hint { margin: 2px 0 0; font-size: 13px; color: var(--eb-muted); }
          @media (max-width: 480px) {
            .cs-identity { flex-direction: column; text-align: center; padding: 20px; gap: 14px; }
            .cs-session { padding: 4px 16px; }
            .cs-session .logout-container, .cs-session .logout-button, .cs-qr .eb-btn { width: 100%; justify-content: center; }
          }
        `}</style>

        <div className="cs-page">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title">Profil</h1>
              <p className="eb-page-subtitle">Votre compte caisse et le QR code de votre établissement.</p>
            </div>
          </header>

          <div className="eb-grid cs-layout">
            <div className="eb-stack">
              {/* Identité */}
              {(profilePhotoURL || companyPseudo || pseudo) && (
              <section className="eb-card cs-identity">
                {profilePhotoURL && (
                  <img src={profilePhotoURL} alt="Profil" className="cs-avatar" />
                )}
                <div className="company-info" style={{ minWidth: 0 }}>
                  {companyPseudo && <p className="cs-company">{companyPseudo}</p>}
                  <p className="cs-user">{pseudo}</p>
                </div>
              </section>
              )}

              {/* ====== Aide ====== */}
              <div id="bottom-cards" className="cs-help">
                {/* Alerte référent (si email) */}
                {referentEmail && (
                  <div className="eb-card cs-help__card">
                    <p className="cs-help__title"><FaLifeRing aria-hidden="true" /> Besoin d'aide&nbsp;?</p>
                    <p className="cs-help__text">
                      Contactez votre référent :{' '}
                      <a href={`mailto:${referentEmail}`}>{referentEmail}</a>
                    </p>
                  </div>
                )}

                {/* Bloc assistance générale */}
                <div className="eb-card cs-help__card">
                  <p className="cs-help__title"><FaEnvelope aria-hidden="true" /> Besoin d'assistance ?</p>
                  <p className="cs-help__text">
                    <a href="mailto:info@ecobill.app">info@ecobill.app</a>
                  </p>
                </div>
              </div>
            </div>

            {/* QR Code + bouton download */}
            {companyUid && (
              <section className="eb-card cs-qr">
                <div className="eb-card__head">
                  <h2 className="eb-card__title cs-section-title">QR code de l'établissement</h2>
                </div>
                <div className="eb-card__body">
                  <QRCodeDisplay
                    value={`https://ecobill.app/redirectqrc/${companyUid}`}
                    caption={shortId ? `ecobill.app/${shortId}` : ''}
                  />
                  <button
                    onClick={handleDownloadQRCode}
                    className="eb-btn eb-btn--secondary"
                    title="Télécharger le QR Code en haute qualité"
                  >
                    <FaDownload size={15} aria-hidden="true" />
                    <span>Télécharger</span>
                  </button>
                </div>
              </section>
            )}
          </div>

          {/* Bouton déconnexion */}
          <section className="eb-card cs-session">
            <div className="eb-row">
              <div className="eb-row__main">
                <p className="cs-session__title">Session</p>
                <p className="cs-session__hint">Se déconnecter de ce compte sur cet appareil.</p>
              </div>
              <div className="eb-row__actions logout-container">
                <button className="logout-button" onClick={handleLogout}>
                  <FaSignOutAlt aria-hidden="true" /> Déconnexion
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CashierSettings;
